'use client';
import React, { useState } from 'react';
import { Eye, EyeOff, ArrowRight, Loader2, Shield, TrendingUp, Lock } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';


type Role = 'manager' | 'investor';

interface LoginFormData {
  email: string;
  password: string;
}

export default function LoginPageClient() {
  const router = useRouter();
  const [selectedRole, setSelectedRole] = useState<Role>('investor');
  const [formData, setFormData] = useState<LoginFormData>({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Partial<LoginFormData>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setErrors((prev) => ({ ...prev, [e.target.name]: undefined }));
    setLoginError(null);
  };

  const validate = (): boolean => {
    const newErrors: Partial<LoginFormData> = {};
    if (!formData.email) {
      newErrors.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Enter a valid email address.';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required.';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsLoading(true);
    setLoginError(null);

    try {
      if (selectedRole === 'investor') {
        const res = await fetch('/api/investor-login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: formData.email, password: formData.password }),
        });
        const body = await res.json();

        if (!res.ok) {
          setLoginError(body.error ?? 'Invalid credentials. Please check your email and password.');
          setIsLoading(false);
          return;
        }

        if (typeof window !== 'undefined') {
          localStorage.setItem('investor_session', JSON.stringify(body.investor));
          // Clear any stale readonly fund session — investor tab always goes to investor dashboard
          localStorage.removeItem('readonly_fund_session');
        }

        // Investor tab always routes to investor dashboard, regardless of readOnlyFundAccess
        router.push('/investor-dashboard');
        return;
      }

      // Fund Manager tab: try Supabase auth first
      const supabase = createClient();
      const { error } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (!error) {
        // Successful fund manager login — clear any stale investor sessions
        if (typeof window !== 'undefined') {
          localStorage.removeItem('readonly_fund_session');
          localStorage.removeItem('investor_session');
        }
        router.push('/fund-manager-dashboard');
        return;
      }

      // Supabase auth failed — check if this is an investor with read-only fund access or full fund manager access
      const res = await fetch('/api/investor-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, password: formData.password }),
      });
      const body = await res.json();

      if (res.ok && body.investor?.fundManagerAccess) {
        // Investor with full fund manager access — grant full fund manager session (not read-only)
        if (typeof window !== 'undefined') {
          localStorage.removeItem('readonly_fund_session');
          localStorage.setItem('investor_session', JSON.stringify(body.investor));
        }
        router.push('/fund-manager-dashboard');
        return;
      }

      if (res.ok && body.investor?.readOnlyFundAccess) {
        // Investor with read-only access — grant read-only fund manager session
        if (typeof window !== 'undefined') {
          localStorage.setItem('readonly_fund_session', JSON.stringify(body.investor));
          localStorage.setItem('investor_session', JSON.stringify(body.investor));
        }
        router.push('/fund-manager-dashboard');
        return;
      }

      // Neither fund manager nor read-only investor — show error
      setLoginError('Invalid credentials. Please check your email and password.');
      setIsLoading(false);
    } catch {
      setLoginError('Something went wrong. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: 'var(--background)' }}>
      {/* Left Brand Panel */}
      <div
        className="hidden lg:flex lg:w-[48%] xl:w-[45%] flex-col justify-between p-12 relative overflow-hidden"
        style={{ backgroundColor: 'var(--secondary)', borderRight: '1px solid var(--border)' }}
      >
        {/* Animated grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `linear-gradient(var(--primary) 1px, transparent 1px), linear-gradient(90deg, var(--primary) 1px, transparent 1px)`,
            backgroundSize: '48px 48px',
          }}
        />

        {/* Large decorative orbs */}
        <div
          className="absolute -top-24 -left-24 w-96 h-96 rounded-full opacity-10"
          style={{
            background: 'radial-gradient(circle, var(--primary) 0%, transparent 70%)',
          }}
        />
        <div
          className="absolute bottom-0 right-0 w-80 h-80 rounded-full opacity-8"
          style={{
            background: 'radial-gradient(circle, var(--accent) 0%, transparent 70%)',
          }}
        />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full opacity-5"
          style={{
            background: 'radial-gradient(circle, var(--primary) 0%, transparent 60%)',
          }}
        />

        {/* Diagonal accent line */}
        <div
          className="absolute top-0 right-0 w-px h-full opacity-20"
          style={{ background: 'linear-gradient(180deg, transparent, var(--primary) 40%, var(--primary) 60%, transparent)' }}
        />

        {/* Top gold rule */}
        <div
          className="absolute top-0 left-0 right-0 h-[2px]"
          style={{ background: 'linear-gradient(90deg, transparent, var(--primary) 30%, var(--primary) 70%, transparent)' }}
        />

        <div className="relative z-10">
          {/* Logo block */}
          <div className="flex flex-col items-start gap-2 mb-14">
            <div className="flex flex-col items-center gap-3">
              {/* Logo with glow ring */}
              <div
                className="relative flex items-center justify-center px-6 py-4"
                style={{
                  borderRadius: '12px',
                  background: 'radial-gradient(ellipse, rgba(200,129,58,0.12) 0%, transparent 70%)',
                  border: '1px solid rgba(200,129,58,0.2)',
                  boxShadow: '0 0 40px rgba(200,129,58,0.1)',
                }}
              >
                <AppLogo size={48} />
              </div>
            </div>
          </div>

          {/* Hero text */}
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-[2px]" style={{ backgroundColor: 'var(--primary)' }} />
              <span className="text-xs font-semibold tracking-[0.2em] uppercase" style={{ color: 'var(--primary)' }}>
                Private Fund Platform
              </span>
            </div>
            <h1
              className="text-5xl xl:text-6xl font-semibold leading-tight"
              style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)', lineHeight: '1.12' }}
            >
              Capital managed
              <br />
              <span style={{ color: 'var(--primary)', fontStyle: 'italic' }}>with precision.</span>
            </h1>
            <p className="text-base leading-relaxed max-w-sm" style={{ color: 'var(--muted-foreground)' }}>
              A private platform for discerning fund managers and their investors.
              Every rupee tracked. Every return accounted for.
            </p>
          </div>

          {/* Feature pills */}
          <div className="flex flex-wrap gap-3 mt-8">
            {[
              { icon: Shield, label: 'Bank-grade Security' },
              { icon: TrendingUp, label: 'Real-time NAV' },
              { icon: Lock, label: 'Private Access' },
            ].map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium"
                style={{
                  backgroundColor: 'rgba(200,129,58,0.08)',
                  border: '1px solid rgba(200,129,58,0.2)',
                  color: 'var(--muted-foreground)',
                }}
              >
                <Icon size={11} style={{ color: 'var(--primary)' }} />
                {label}
              </div>
            ))}
          </div>
        </div>

        {/* Stats removed */}
      </div>

      {/* Right Form Panel */}
      <div
        className="flex-1 flex flex-col items-center justify-center p-8 lg:p-12 relative overflow-hidden"
        style={{ backgroundColor: 'var(--background)' }}
      >
        {/* Subtle background texture on right panel */}
        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage: `radial-gradient(circle at 80% 20%, var(--primary) 0%, transparent 50%), radial-gradient(circle at 20% 80%, var(--accent) 0%, transparent 50%)`,
          }}
        />

        {/* Back to landing page */}
        <a
          href="/"
          className="absolute top-6 left-6 z-10 flex items-center gap-2 text-sm font-medium transition-all duration-200 hover:opacity-100 opacity-70"
          style={{ color: 'var(--muted-foreground)' }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 5l-7 7 7 7" />
          </svg>
          Back
        </a>

        {/* Mobile logo */}
        <div className="flex items-center gap-3 mb-10 lg:hidden relative z-10">
          <div
            className="flex items-center justify-center rounded-full"
            style={{
              width: 48,
              height: 48,
              background: 'radial-gradient(circle, rgba(200,129,58,0.15) 0%, transparent 70%)',
              border: '1px solid rgba(200,129,58,0.25)',
            }}
          >
            <AppLogo size={32} />
          </div>
          <span className="font-serif text-xl font-semibold tracking-wide" style={{ color: 'var(--primary)', fontFamily: 'var(--font-serif)' }}>
            MACRO CAPITAL
          </span>
        </div>

        <div className="w-full max-w-md relative z-10">
          {/* Form card with glass effect */}
          <div
            className="rounded-2xl p-8"
            style={{
              backgroundColor: 'var(--card)',
              border: '1px solid var(--border)',
              boxShadow: '0 0 0 1px rgba(200,129,58,0.06), 0 24px 64px rgba(0,0,0,0.4), 0 8px 24px rgba(0,0,0,0.3)',
            }}
          >
            {/* Top accent line on card */}
            <div
              className="absolute top-0 left-8 right-8 h-[1px] rounded-full"
              style={{ background: 'linear-gradient(90deg, transparent, var(--primary), transparent)' }}
            />

            {/* Form header */}
            <div className="mb-7">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-4 h-[2px]" style={{ backgroundColor: 'var(--primary)' }} />
                <span className="text-xs font-semibold tracking-[0.15em] uppercase" style={{ color: 'var(--primary)' }}>
                  Secure Access
                </span>
              </div>
              <h2
                className="text-3xl font-semibold mb-2"
                style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}
              >
                Welcome back
              </h2>
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                Sign in to access your private fund portal.
              </p>
            </div>

            {/* Role Toggle */}
            <div
              className="flex rounded-xl p-1 mb-6"
              style={{ backgroundColor: 'var(--muted)', border: '1px solid var(--border)' }}
              role="tablist"
              aria-label="Select account role"
            >
              {(['manager', 'investor'] as Role[]).map((role) => (
                <button
                  key={`role-tab-${role}`}
                  role="tab"
                  aria-selected={selectedRole === role}
                  onClick={() => { setSelectedRole(role); setLoginError(null); }}
                  className="flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all duration-200"
                  style={{
                    backgroundColor: selectedRole === role ? 'var(--accent)' : 'transparent',
                    color: selectedRole === role ? 'var(--primary)' : 'var(--muted-foreground)',
                    border: selectedRole === role ? '1px solid rgba(200,129,58,0.3)' : '1px solid transparent',
                    boxShadow: selectedRole === role ? '0 2px 8px rgba(0,0,0,0.3)' : 'none',
                  }}
                >
                  {role === 'manager' ? 'Fund Manager' : 'Investor'}
                </button>
              ))}
            </div>

            {/* Login error */}
            {loginError && (
              <div
                className="mb-5 px-4 py-3 rounded-lg text-sm"
                style={{
                  backgroundColor: 'rgba(139, 46, 46, 0.2)',
                  border: '1px solid rgba(240, 160, 160, 0.25)',
                  color: '#F0A0A0',
                }}
              >
                {loginError}
              </div>
            )}

            {/* Form */}
            <form onSubmit={onSubmit} className="space-y-5" noValidate>
              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-xs font-semibold mb-2 tracking-wide uppercase"
                  style={{ color: 'var(--muted-foreground)' }}
                >
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  className="w-full px-4 py-3 rounded-lg text-sm transition-all duration-200 outline-none"
                  style={{
                    backgroundColor: 'var(--input)',
                    border: errors.email ? '1px solid rgba(240,160,160,0.5)' : '1px solid var(--border)',
                    color: 'var(--foreground)',
                    boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.2)',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.border = '1px solid rgba(200,129,58,0.5)';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(200,129,58,0.08), inset 0 1px 3px rgba(0,0,0,0.2)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.border = errors.email ? '1px solid rgba(240,160,160,0.5)' : '1px solid var(--border)';
                    e.currentTarget.style.boxShadow = 'inset 0 1px 3px rgba(0,0,0,0.2)';
                  }}
                />
                {errors.email && (
                  <p className="mt-1.5 text-xs" style={{ color: '#F0A0A0' }}>{errors.email}</p>
                )}
              </div>

              {/* Password */}
              <div>
                <label
                  htmlFor="password"
                  className="block text-xs font-semibold mb-2 tracking-wide uppercase"
                  style={{ color: 'var(--muted-foreground)' }}
                >
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    className="w-full px-4 py-3 pr-12 rounded-lg text-sm transition-all duration-200 outline-none"
                    style={{
                      backgroundColor: 'var(--input)',
                      border: errors.password ? '1px solid rgba(240,160,160,0.5)' : '1px solid var(--border)',
                      color: 'var(--foreground)',
                      boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.2)',
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.border = '1px solid rgba(200,129,58,0.5)';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(200,129,58,0.08), inset 0 1px 3px rgba(0,0,0,0.2)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.border = errors.password ? '1px solid rgba(240,160,160,0.5)' : '1px solid var(--border)';
                      e.currentTarget.style.boxShadow = 'inset 0 1px 3px rgba(0,0,0,0.2)';
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded transition-colors"
                    style={{ color: 'var(--muted-foreground)' }}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1.5 text-xs" style={{ color: '#F0A0A0' }}>{errors.password}</p>
                )}
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-200 mt-2"
                style={{
                  background: isLoading
                    ? 'var(--muted)'
                    : 'linear-gradient(135deg, var(--primary) 0%, var(--gold-dark) 100%)',
                  color: isLoading ? 'var(--muted-foreground)' : 'var(--primary-foreground)',
                  border: 'none',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  boxShadow: isLoading ? 'none' : '0 4px 16px rgba(200,129,58,0.3), 0 1px 3px rgba(0,0,0,0.3)',
                  letterSpacing: '0.03em',
                }}
              >
                {isLoading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Signing in…
                  </>
                ) : (
                  <>
                    Sign In
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="flex items-center gap-3 mt-6">
              <div className="flex-1 h-px" style={{ backgroundColor: 'var(--border)' }} />
              <span className="text-xs" style={{ color: 'var(--muted-foreground)', opacity: 0.6 }}>secured by</span>
              <div className="flex-1 h-px" style={{ backgroundColor: 'var(--border)' }} />
            </div>

            {/* Security badge */}
            <div className="flex items-center justify-center gap-2 mt-4">
              <Lock size={11} style={{ color: 'var(--muted-foreground)', opacity: 0.5 }} />
              <span className="text-xs" style={{ color: 'var(--muted-foreground)', opacity: 0.5 }}>
                256-bit encrypted · Private access only
              </span>
            </div>
          </div>

          {/* Investor login link */}
          <p className="text-center text-xs mt-5" style={{ color: 'var(--muted-foreground)', opacity: 0.6 }}>
            Investor access is by invitation only.{' '}
            <span style={{ color: 'var(--primary)', opacity: 0.8 }}>Contact your fund manager.</span>
          </p>
        </div>
      </div>
    </div>
  );
}